import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import type {
  EnergyResolution,
  FlowBucket,
  FlowReport,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import {
  formatDateTime,
  formatNumber,
  formatRange,
  formatShare,
} from "../format.ts";
import { range, scope } from "../context.ts";
import {
  balanced,
  flowsNow,
  type Flow,
  type FlowLabels,
  type Flows,
} from "../overview.ts";
import { Icon } from "../icons.tsx";
import { Sankey } from "../components/Sankey.tsx";
import { SupplyTopology } from "../components/SupplyTopology.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { PageHeader, Skeleton } from "../components/ui.tsx";

/** Unmetered share above which the balance needs more sub-metering (the host's EnergyFlowCalculator.UnmeteredThreshold). */
const THRESHOLD = 0.1;

type Mode = "now" | "period";

/** Buckets of the quality chart: hours within a day or two, days otherwise. */
const resolutionFor = (from: Date, to: Date): EnergyResolution =>
  (to.getTime() - from.getTime()) / 86_400_000 <= 2 ? "hour" : "day";

/**
 * Media flows (prototype "Przepływy mediów"): the logical balance of the scope's boundary, what came in and where it
 * went, now or over the period, and how much of it no sub-meter measures. It does not show the physical route.
 */
export function FlowsPage() {
  const navigate = useNavigate();
  const [mode, setMode] = createSignal<Mode>("period");
  const [view, setView] = createSignal<"logic" | "topology">("logic");
  const nodeId = () => scope()?.id;
  const resolution = () => resolutionFor(range().from, range().to);
  const [report] = createResource(
    () =>
      nodeId() && mode() === "period"
        ? { id: nodeId()!, ...range(), res: resolution() }
        : undefined,
    (q) => api.sites.flows(q.id, q.from, q.to, q.res),
  );
  const [now] = createResource(
    () => (nodeId() && mode() === "now" ? nodeId() : undefined),
    (id) => api.sites.now(id),
  );
  const labels = () => ({
    grid: t("overview.grid"),
    pv: t("overview.production"),
    export: t("overview.toGrid"),
    rest: t("overview.unmetered"),
    unmeasured: t("overview.unmeasured"),
  });
  const flows = createMemo<Flows | null>(() => {
    if (mode() === "now") {
      return now() ? flowsNow(now()!, labels()) : null;
    }
    const r = report();
    return r?.hasBoundary && r.consumption !== null
      ? periodFlows(r, labels())
      : null;
  });
  const unit = () => (mode() === "now" ? "kW" : "kWh");
  const open = (pointId: string) =>
    navigate(`/assets?point=${encodeURIComponent(pointId)}`);
  const pending = () => (mode() === "now" ? now.loading : report.loading);
  const failure = () => (mode() === "now" ? now.error : report.error);
  const boundary = () =>
    mode() === "now" ? now()?.hasBoundary : report()?.hasBoundary;

  return (
    <>
      <PageHeader
        eyebrow={`${t("flows.eyebrow")} · ${scope()?.name ?? ""} · ${mode() === "now" ? t("flows.mode.now") : formatRange(range().from, range().to, locale())}`}
        title={t("flows.title")}
        sub={t("flows.intro")}
      >
        <div class="seg" role="group" aria-label={t("flows.viewLabel")}>
          <For each={["logic", "topology"] as const}>
            {(v) => (
              <button
                type="button"
                aria-pressed={view() === v}
                onClick={() => setView(v)}
              >
                {t(`flows.view.${v}`)}
              </button>
            )}
          </For>
        </div>
        <div
          class="seg"
          role="group"
          aria-label={t("flows.modeLabel")}
          hidden={view() === "topology"}
        >
          <For each={["now", "period"] as const}>
            {(m) => (
              <button
                type="button"
                aria-pressed={mode() === m}
                onClick={() => setMode(m)}
              >
                {t(`flows.mode.${m}`)}
              </button>
            )}
          </For>
        </div>
      </PageHeader>
      <Show
        when={view() === "logic"}
        fallback={
          <section class="card">
            <div class="card-h">
              <h2>{t("supply.title")}</h2>
              <span class="meta">{t("supply.meta")}</span>
            </div>
            <Show when={nodeId()}>
              {(id) => <SupplyTopology nodeId={id()} />}
            </Show>
          </section>
        }
      >
        <Show when={!failure()} fallback={<ErrorBox error={failure()} />}>
          <Show
            when={!pending() || flows()}
            fallback={
              <section class="card">
                <Skeleton lines={6} />
              </section>
            }
          >
            <Show
              when={boundary() !== false}
              fallback={
                <section class="card">
                  <div class="note">
                    <Icon name="alert" />
                    <span>{t("overview.noBoundary")}</span>
                    <a
                      class="btn sm"
                      href="/assets"
                      style={{ "margin-left": "auto" }}
                    >
                      {t("overview.toAssets")}
                    </a>
                  </div>
                </section>
              }
            >
              <div class="grid g-2">
                <section class="card">
                  <div class="card-h">
                    <h2>{t("flows.balanceTitle")}</h2>
                    <span class="meta">{t("flows.balanceMeta")}</span>
                  </div>
                  <Show
                    when={flows()}
                    fallback={
                      <div class="card-b hint">{t("overview.noData")}</div>
                    }
                  >
                    {(f) => (
                      <>
                        <div class="card-b">
                          <Sankey
                            flows={f()}
                            center={scope()?.name ?? ""}
                            unit={unit()}
                            locale={locale()}
                            label={t("flows.balanceTitle")}
                            restNote={t("overview.calculated")}
                            onPick={open}
                          />
                        </div>
                        <OutletTable flows={f()} unit={unit()} onPick={open} />
                        <div
                          class="note"
                          style={{
                            "border-top": "1px solid var(--line)",
                            "border-bottom": "0",
                          }}
                        >
                          <Icon name="flow" />
                          {mode() === "now"
                            ? t("flows.noteNow", {
                                time: now()?.at
                                  ? formatDateTime(now()!.at!, locale())
                                  : "—",
                              })
                            : t("flows.notePeriod", {
                                complete: report()?.completeBuckets ?? 0,
                                total: report()?.buckets.length ?? 0,
                              })}
                        </div>
                      </>
                    )}
                  </Show>
                </section>
                <Show
                  when={mode() === "period" && report()}
                  fallback={<QualityHint />}
                >
                  {(r) => <Quality report={r()} />}
                </Show>
              </div>
            </Show>
          </Show>
        </Show>
      </Show>
    </>
  );
}

/** The period's balance as flows, in kWh over the complete buckets. */
function periodFlows(r: FlowReport, labels: FlowLabels): Flows {
  const ins: Flow[] = [
    { key: "grid", label: labels.grid, kw: r.import ?? 0, kind: "grid" },
  ];
  if (r.production !== null) {
    ins.push({ key: "pv", label: labels.pv, kw: r.production, kind: "pv" });
  }
  const outs: Flow[] = r.outlets
    .filter((o) => o.kwh !== null)
    .map((o) => ({
      key: o.pointId,
      label: o.name,
      kw: o.kwh!,
      kind: "load",
      pointId: o.pointId,
    }));
  if ((r.export ?? 0) > 0) {
    outs.push({
      key: "export",
      label: labels.export,
      kw: r.export!,
      kind: "export",
    });
  }
  if ((r.unmetered ?? 0) > 0) {
    outs.push({
      key: "rest",
      label: labels.rest,
      kw: r.unmetered!,
      kind: "rest",
    });
  }
  return balanced(
    { ins, outs, total: r.consumption ?? 0 },
    labels.unmeasured ?? labels.rest,
  );
}

function OutletTable(props: {
  flows: Flows;
  unit: string;
  onPick: (pointId: string) => void;
}) {
  const rows = () =>
    props.flows.outs.filter((o) => o.kind === "load" || o.kind === "rest");
  const share = (kw: number) =>
    props.flows.total > 0 ? kw / props.flows.total : null;
  return (
    <Show when={rows().length > 0}>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th>{t("flows.outlet")}</th>
              <th class="r">{props.unit}</th>
              <th class="r">{t("flows.ofConsumption")}</th>
            </tr>
          </thead>
          <tbody>
            <For each={rows()}>
              {(o) => (
                <tr
                  class={o.pointId ? "click" : ""}
                  onClick={() => o.pointId && props.onPick(o.pointId)}
                >
                  <td>
                    {o.label}
                    <Show when={o.kind === "rest"}>
                      <div class="hint">{t("flows.restHint")}</div>
                    </Show>
                  </td>
                  <td class="r num">{formatNumber(o.kw, locale(), 2)}</td>
                  <td class="r num">{formatShare(share(o.kw), locale())}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </Show>
  );
}

function QualityHint() {
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("flows.quality")}</h2>
      </div>
      <div class="card-b hint">{t("flows.qualityNowHint")}</div>
    </section>
  );
}

/** Unmetered share per bucket against the threshold, and what it means for metering. */
function Quality(props: { report: FlowReport }) {
  // Same drawing width as the other charts, so text keeps its size when the card is wide.
  const W = 800;
  const H = 220;
  const L = 44;
  const B = 190;
  const buckets = () => props.report.buckets;
  const top = () =>
    Math.max(0.15, ...buckets().map((b) => b.unmeteredShare ?? 0)) * 1.1;
  const y = (share: number) => B - (share / top()) * (B - 12);
  const slot = () => (W - L - 6) / Math.max(1, buckets().length);
  // Grid lines far enough apart for their labels; 0 always stays.
  const ticks = () =>
    [1, 0.75, 0.5, 0.25, 0.15, 0.1, 0.05, 0]
      .filter((v) => v <= top())
      .reduce<number[]>((kept, v) => {
        const last = kept[kept.length - 1];
        if (last === undefined || y(v) - y(last) >= 18) {
          return [...kept, v];
        }
        return v === 0 ? [...kept.slice(0, -1), v] : kept;
      }, []);
  const label = (at: string) => {
    const d = new Date(at);
    return props.report.resolution === "hour"
      ? `${String(d.getHours()).padStart(2, "0")}:00`
      : String(d.getDate());
  };
  const worst = () =>
    buckets().reduce<FlowBucket | null>(
      (w, b) =>
        b.unmeteredShare !== null &&
        (w === null || b.unmeteredShare > w.unmeteredShare!)
          ? b
          : w,
      null,
    );
  const above = () =>
    buckets().filter((b) => (b.unmeteredShare ?? 0) > THRESHOLD).length;
  const step = () => Math.max(1, Math.ceil(buckets().length / 12));

  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("flows.quality")}</h2>
        <span class="meta">
          {t(
            `flows.qualityMeta.${props.report.resolution === "hour" ? "hour" : "day"}`,
          )}
        </span>
      </div>
      <div class="card-b">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            "font-family": "var(--font-mono)",
            "font-size": "10px",
          }}
          role="img"
          aria-label={t("flows.qualityChart")}
        >
          <For each={ticks()}>
            {(v) => (
              <>
                <line
                  x1={L}
                  x2={W - 5}
                  y1={y(v)}
                  y2={y(v)}
                  stroke="var(--line)"
                />
                <text
                  x={L - 6}
                  y={y(v) + 3}
                  text-anchor="end"
                  fill="var(--faint)"
                >
                  {Math.round(v * 100)}%
                </text>
              </>
            )}
          </For>
          <For each={buckets()}>
            {(b, i) => {
              const x = () => L + 4 + i() * slot();
              const w = () => Math.max(2, slot() - (slot() > 8 ? 6 : 1));
              return (
                <>
                  <Show
                    when={b.complete && b.unmeteredShare !== null}
                    fallback={
                      <rect
                        x={x()}
                        y={B - 4}
                        width={w()}
                        height={4}
                        fill="var(--line)"
                      >
                        <title>{t("flows.incompleteBucket")}</title>
                      </rect>
                    }
                  >
                    <rect
                      x={x()}
                      y={y(b.unmeteredShare!)}
                      width={w()}
                      height={Math.max(0.5, B - y(b.unmeteredShare!))}
                      fill={
                        b.unmeteredShare! > THRESHOLD
                          ? "var(--warn)"
                          : "var(--faint)"
                      }
                      fill-opacity="0.55"
                    >
                      <title>
                        {formatDateTime(b.at, locale())}:{" "}
                        {formatShare(b.unmeteredShare, locale())} (
                        {formatNumber(b.unmetered, locale(), 2)} kWh)
                      </title>
                    </rect>
                  </Show>
                  <Show when={i() % step() === 0}>
                    <text
                      x={x() + w() / 2}
                      y={B + 18}
                      text-anchor="middle"
                      fill="var(--faint)"
                    >
                      {label(b.at)}
                    </text>
                  </Show>
                </>
              );
            }}
          </For>
          {/* Over the bars, so the threshold stays visible when most of them exceed it. */}
          <line
            x1={L}
            x2={W - 5}
            y1={y(THRESHOLD)}
            y2={y(THRESHOLD)}
            stroke="var(--warn)"
            stroke-width="1.5"
            stroke-dasharray="6 4"
          />
          <text
            x={W - 8}
            y={y(THRESHOLD) - 6}
            text-anchor="end"
            fill="var(--warn)"
            stroke="var(--surface)"
            stroke-width="4"
            stroke-linejoin="round"
            paint-order="stroke"
          >
            {t("flows.threshold", { share: "10%" })}
          </text>
        </svg>
        <dl class="kv" style={{ "margin-top": "10px" }}>
          <dt>{t("flows.period")}</dt>
          <dd>
            <Show when={props.report.unmeteredShare !== null} fallback="—">
              {t("flows.periodShare", {
                share: formatShare(props.report.unmeteredShare, locale()),
                kwh: formatNumber(props.report.unmetered, locale(), 1),
              })}
            </Show>
          </dd>
          <dt>{t("flows.coverage")}</dt>
          <dd>
            {t("flows.coverageText", {
              complete: props.report.completeBuckets,
              total: props.report.buckets.length,
            })}
          </dd>
          <dt>{t("flows.aboveThreshold")}</dt>
          <dd>
            {above()}
            <Show when={worst()}>
              {(w) =>
                ` · ${t("flows.worst", { at: formatDateTime(w().at, locale()), share: formatShare(w().unmeteredShare, locale()) })}`
              }
            </Show>
          </dd>
          <dt>{t("flows.outlets")}</dt>
          <dd>
            {props.report.outlets.length === 0
              ? t("flows.noOutlets")
              : props.report.outlets.map((o) => o.name).join(", ")}
          </dd>
        </dl>
        <Show when={(props.report.unmeteredShare ?? 0) > THRESHOLD}>
          <p class="hint">{t("flows.recommendation")}</p>
        </Show>
      </div>
    </section>
  );
}
